import React, { useMemo } from 'react';
import { Trophy, Award, Star, Target } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import styles from './UserLevel.module.css';

const LEVELS = [
    {
        name: 'Beginner',
        xp: 0,
        icon: Trophy,
        challenge: {
            title: 'Greet 3 Strangers',
            description: 'Break social hesitation and make initiating conversations easier.',
            icon: Target
        }
    },
    {
        name: 'Novice',
        xp: 500,
        icon: Trophy,
        challenge: {
            title: 'Deep Conversation Challenge',
            description: 'Keep a conversation going with a friend for at least 5 minutes using open-ended questions.',
            icon: Target
        }
    },
    {
        name: 'Intermediate',
        xp: 2000,
        icon: Trophy,
        challenge: {
            title: 'Small Talk Mastery',
            description: 'Maintain small talk for at least 30 seconds while ordering something in person.',
            icon: Target
        }
    },
    {
        name: 'Advanced',
        xp: 5000,
        icon: Trophy,
        challenge: {
            title: 'Conversation Leadership',
            description: 'Lead a phone conversation with a friend or family member for at least 10 minutes.',
            icon: Target
        }
    },
    {
        name: 'Proficient',
        xp: 10000,
        icon: Award,
        challenge: {
            title: 'Concept Explanation',
            description: 'Explain a complex concept or skill clearly and confidently to a friend.',
            icon: Target
        }
    },
    {
        name: 'Expert',
        xp: 18000,
        icon: Award,
        challenge: {
            title: 'Stranger Conversation',
            description: 'Start and sustain a meaningful conversation with a stranger for at least 3 minutes.',
            icon: Target
        }
    },
    {
        name: 'Elite',
        xp: 28000,
        icon: Award,
        challenge: {
            title: 'Group Discussion Leader',
            description: 'Organize and lead a group discussion or debate with 3-4 people.',
            icon: Target
        }
    },
    {
        name: 'Master',
        xp: 40000,
        icon: Star,
        challenge: {
            title: 'Public Speaking Milestone',
            description: 'Deliver a 5-minute structured talk or speech to a small audience.',
            icon: Target
        }
    },
    {
        name: 'Grandmaster',
        xp: 55000,
        icon: Star,
        challenge: {
            title: 'Workshop Hosting',
            description: 'Host a public speaking session or workshop for at least 10 minutes.',
            icon: Target
        }
    },
    {
        name: 'Legendary',
        xp: 75000,
        icon: Star,
        challenge: {
            title: 'Coaching Mastery',
            description: 'Teach others how to give effective presentations and improve public speaking.',
            icon: Target
        }
    }
];

const UserLevel = () => {
    const { user } = useAuth();
    const totalXp = user?.total_xp || 0;

    const currentLevel = useMemo(() => {
        const level = LEVELS.reduce((prev, curr) => {
            if (totalXp >= curr.xp) return curr;
            return prev;
        }, LEVELS[0]);

        const nextLevel = LEVELS[LEVELS.indexOf(level) + 1];
        const progress = nextLevel
            ? ((totalXp - level.xp) / (nextLevel.xp - level.xp)) * 100
            : 100;

        return {
            ...level,
            nextLevel,
            progress: Math.min(progress, 100)
        };
    }, [totalXp]);

    const IconComponent = currentLevel.icon;
    const ChallengeIcon = currentLevel.challenge.icon;

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <IconComponent className={styles.icon} size={20} />
                <h3>Proficiency Level</h3>
            </div>
            <div className={styles.content}>
                <div className={styles.levelInfo}>
                    <div className={styles.currentLevel}>
                        <div className={styles.levelDetails}>
                            <span className={styles.levelName}>{currentLevel.name}</span>
                            <span className={styles.levelLabel}>Current Level</span>
                        </div>
                        <span className={styles.xpCount}>{totalXp.toLocaleString()} XP</span>
                    </div>

                    {/* Level Challenge Section */}
                    <div className={styles.challengeContainer}>
                        <div className={styles.challengeHeader}>
                            <ChallengeIcon className={styles.challengeIcon} size={20} />
                            <span className={styles.challengeTitle}>Level Challenge</span>
                        </div>
                        <div className={styles.challengeContent}>
                            <div className={styles.challengeDetails}>
                                <h4 className={styles.challengeName}>{currentLevel.challenge.title}</h4>
                                <p className={styles.challengeDescription}>
                                    {currentLevel.challenge.description}
                                </p>
                            </div>
                        </div>
                    </div>

                    {currentLevel.nextLevel && (
                        <div className={styles.progressContainer}>
                            <div className={styles.progressHeader}>
                                <span className={styles.progressLabel}>Progress to Next Level</span>
                                <span className={styles.progressPercent}>
                                    {Math.round(currentLevel.progress)}%
                                </span>
                            </div>
                            <div className={styles.progressBar}>
                                <div
                                    className={styles.progress}
                                    style={{ width: `${currentLevel.progress}%` }}
                                />
                            </div>
                            <div className={styles.nextLevel}>
                                <div className={styles.nextLevelInfo}>
                                    <span className={styles.nextLevelName}>{currentLevel.nextLevel.name}</span>
                                    <span className={styles.nextLevelLabel}>Next Level</span>
                                </div>
                                <span className={styles.nextLevelXp}>
                                    {currentLevel.nextLevel.xp.toLocaleString()} XP
                                </span>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default UserLevel;